/* eslint-disable no-unused-vars, jsx-a11y/label-has-associated-control, object-shorthand */
import React, { useState } from 'react'
import {
	Table,
	Button,
	Space,
	Tag,
	Tooltip,
	Popconfirm,
	message,
	Typography,
	Card,
	Row,
	Col,
	InputNumber,
	Modal,
} from 'antd'
import {
	EditOutlined,
	DeleteOutlined,
	CopyOutlined,
	ExclamationCircleOutlined,
} from '@ant-design/icons'
import { MessagingPrice, CurrencyType } from 'types'
import pricingService from 'api/pricing/pricingService'

const { Text } = Typography

// eslint-disable-next-line no-unused-vars
interface PricingTableProps {
	data: MessagingPrice[]
	loading: boolean
	totalCount: number
	currentPage: number
	pageSize: number
	onPaginationChange: (page: number, size?: number) => void
	onEdit: (record: MessagingPrice) => void
	onDelete: (id: string) => void
	onBulkUpdate: (updates: Array<{ _id: string; data: any }>) => void
}

/**
 * Bulk Edit Form Component
 */
// eslint-disable-next-line no-unused-vars
const BulkEditForm: React.FC<{
	onSubmit: (formData: any) => void
	onCancel: () => void
}> = ({ onSubmit, onCancel }) => {
	const [formData, setFormData] = useState<any>({})

	const handleSubmit = () => {
		onSubmit(formData)
	}

	return (
		<div>
			<Row gutter={16}>
				<Col span={12}>
					<label htmlFor='vendor-charge'>Vendor Charge</label>
					<InputNumber
						id='vendor-charge'
						style={{ width: '100%' }}
						precision={4}
						min={0}
						step={0.001}
						onChange={value => setFormData({ ...formData, vendor_charge: value })}
					/>
				</Col>
				<Col span={12}>
					<label htmlFor='whatcx-charge'>WhatCX Charge</label>
					<InputNumber
						id='whatcx-charge'
						style={{ width: '100%' }}
						precision={4}
						min={0}
						step={0.001}
						onChange={value => setFormData({ ...formData, whatcx_charge: value })}
					/>
				</Col>
			</Row>
			<Row gutter={16} style={{ marginTop: 16 }}>
				<Col span={12}>
					<Button type='primary' onClick={handleSubmit}>
						Apply Changes
					</Button>
				</Col>
				<Col span={12}>
					<Button onClick={onCancel}>Cancel</Button>
				</Col>
			</Row>
		</div>
	)
}

/**
 * Pricing Table Component
 * Displays pricing data with inline editing capabilities
 */
const PricingTable: React.FC<PricingTableProps> = ({
	data,
	loading,
	totalCount,
	currentPage,
	pageSize,
	onPaginationChange,
	onEdit,
	onDelete,
	onBulkUpdate,
}) => {
	const [editingCell, setEditingCell] = useState<{
		key: string
		field: string
	} | null>(null)
	const [editingValue, setEditingValue] = useState<number>(0)
	const [bulkEditVisible, setBulkEditVisible] = useState(false)
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

	/**
	 * Handle inline cell editing
	 */
	const handleCellEdit = (
		record: MessagingPrice,
		field: string,
		value: number
	) => {
		setEditingCell({ key: record._id!, field })
		setEditingValue(value)
	}

	/**
	 * Handle inline cell save
	 */
	const handleCellSave = async (record: MessagingPrice, field: string) => {
		try {
			await pricingService.updatePricing(record._id!, { [field]: editingValue })
			message.success('Pricing updated successfully')
			setEditingCell(null)
			// Refresh data by calling parent's refresh function
			window.location.reload() // Simple refresh for now
		} catch (error) {
			message.error('Failed to update pricing')
			setEditingCell(null)
		}
	}

	/**
	 * Handle inline cell cancel
	 */
	const handleCellCancel = () => {
		setEditingCell(null)
		setEditingValue(0)
	}

	/**
	 * Handle duplicate pricing
	 */
	const handleDuplicate = async (record: MessagingPrice) => {
		try {
			await pricingService.duplicatePricing(
				record._id!,
				record.vendor,
				record.base_currency
			)
			message.success('Pricing duplicated successfully')
			window.location.reload()
		} catch (error) {
			message.error('Failed to duplicate pricing')
		}
	}

	/**
	 * Handle bulk edit
	 */
	const handleBulkEdit = () => {
		if (selectedRowKeys.length === 0) {
			message.warning('Please select records to edit')
			return
		}
		setBulkEditVisible(true)
	}

	/**
	 * Handle bulk edit submit
	 */
	const handleBulkEditSubmit = async (updates: any) => {
		try {
			const bulkUpdates = selectedRowKeys.map(key => ({
				_id: key as string,
				data: updates,
			}))
			await onBulkUpdate(bulkUpdates)
			setBulkEditVisible(false)
			setSelectedRowKeys([])
		} catch (error) {
			message.error('Failed to perform bulk update')
		}
	}

	/**
	 * Render editable cell
	 */
	const renderEditableCell = (
		value: number,
		record: MessagingPrice,
		field: string
	) => {
		const isEditing =
			editingCell?.key === record._id && editingCell?.field === field

		if (isEditing) {
			return (
				<Space>
					<InputNumber
						value={editingValue}
						onChange={val => setEditingValue(val || 0)}
						precision={4}
						min={0}
						step={0.001}
						style={{ width: 80 }}
					/>
					<Button
						size='small'
						type='primary'
						onClick={() => handleCellSave(record, field)}
					>
						Save
					</Button>
					<Button size='small' onClick={handleCellCancel}>
						Cancel
					</Button>
				</Space>
			)
		}

		return (
			<div
				onClick={() => handleCellEdit(record, field, value)}
				style={{ cursor: 'pointer', padding: '4px 8px' }}
				title='Click to edit'
				role='button'
				tabIndex={0}
				onKeyDown={e => {
					if (e.key === 'Enter' || e.key === ' ') {
						handleCellEdit(record, field, value)
					}
				}}
			>
				{value?.toFixed(4) || '0.0000'}
			</div>
		)
	}

	/**
	 * Table columns configuration
	 */
	const columns = [
		{
			title: 'Country',
			dataIndex: 'country',
			key: 'country',
			width: 120,
			fixed: 'left' as const,
			render: (country: string, record: MessagingPrice) => (
				<Space direction='vertical' size={0}>
					<Text strong>{country}</Text>
					<Space>
						<Tag color='blue'>{record.country_code}</Tag>
						<Tag color='green'>+{record.phone_code}</Tag>
					</Space>
				</Space>
			),
		},
		{
			title: 'Vendor',
			dataIndex: 'vendor',
			key: 'vendor',
			width: 120,
			render: (vendor: string) => <Tag color='purple'>{vendor}</Tag>,
		},
		{
			title: 'Currency',
			dataIndex: 'base_currency',
			key: 'base_currency',
			width: 80,
			render: (currency: CurrencyType) => <Tag color='gold'>{currency}</Tag>,
		},
		{
			title: 'Channel',
			dataIndex: 'channel',
			key: 'channel',
			width: 100,
			render: (channel: string) => <Tag color='cyan'>{channel}</Tag>,
		},
		{
			title: 'Vendor Charge',
			dataIndex: 'vendor_charge',
			key: 'vendor_charge',
			width: 120,
			render: (value: number, record: MessagingPrice) =>
				renderEditableCell(value, record, 'vendor_charge'),
		},
		{
			title: 'WhatCX Charge',
			dataIndex: 'whatcx_charge',
			key: 'whatcx_charge',
			width: 120,
			render: (value: number, record: MessagingPrice) =>
				renderEditableCell(value, record, 'whatcx_charge'),
		},
		{
			title: 'UIC Charge',
			dataIndex: 'uic_charge',
			key: 'uic_charge',
			width: 100,
			render: (value: number, record: MessagingPrice) =>
				renderEditableCell(value, record, 'uic_charge'),
		},
		{
			title: 'BIC Charge',
			dataIndex: 'bic_charge',
			key: 'bic_charge',
			width: 100,
			render: (value: number, record: MessagingPrice) =>
				renderEditableCell(value, record, 'bic_charge'),
		},
		{
			title: 'Media Charge',
			dataIndex: 'media_charge',
			key: 'media_charge',
			width: 120,
			render: (value: number, record: MessagingPrice) =>
				renderEditableCell(value, record, 'media_charge'),
		},
		{
			title: 'Template Marketing',
			dataIndex: 'whatsapp_template_marketing',
			key: 'whatsapp_template_marketing',
			width: 140,
			render: (value: number, record: MessagingPrice) =>
				renderEditableCell(value, record, 'whatsapp_template_marketing'),
		},
		{
			title: 'Template Auth',
			dataIndex: 'whatsapp_template_authentication',
			key: 'whatsapp_template_authentication',
			width: 120,
			render: (value: number, record: MessagingPrice) =>
				renderEditableCell(value, record, 'whatsapp_template_authentication'),
		},
		{
			title: 'Template Utility',
			dataIndex: 'whatsapp_template_utility',
			key: 'whatsapp_template_utility',
			width: 120,
			render: (value: number, record: MessagingPrice) =>
				renderEditableCell(value, record, 'whatsapp_template_utility'),
		},
		{
			title: 'WhatsApp Service',
			dataIndex: 'whatsapp_service',
			key: 'whatsapp_service',
			width: 120,
			render: (value: number, record: MessagingPrice) =>
				renderEditableCell(value, record, 'whatsapp_service'),
		},
		{
			title: 'Actions',
			key: 'actions',
			width: 120,
			fixed: 'right' as const,
			render: (_: any, record: MessagingPrice) => (
				<Space>
					<Tooltip title='Edit'>
						<Button
							type='link'
							icon={<EditOutlined />}
							onClick={() => onEdit(record)}
						/>
					</Tooltip>
					<Tooltip title='Duplicate'>
						<Button
							type='link'
							icon={<CopyOutlined />}
							onClick={() => handleDuplicate(record)}
						/>
					</Tooltip>
					<Tooltip title='Delete'>
						<Popconfirm
							title='Are you sure you want to delete this pricing entry?'
							onConfirm={() => onDelete(record._id!)}
							okText='Yes'
							cancelText='No'
							icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
						>
							<Button type='link' danger icon={<DeleteOutlined />} />
						</Popconfirm>
					</Tooltip>
				</Space>
			),
		},
	]

	/**
	 * Row selection configuration
	 */
	const rowSelection = {
		selectedRowKeys,
		onChange: setSelectedRowKeys,
		getCheckboxProps: (record: MessagingPrice) => ({
			name: record._id,
		}),
	}

	return (
		<>
			<Card>
				<Row justify='space-between' align='middle' style={{ marginBottom: 16 }}>
					<Col>
						<Typography.Title level={4} style={{ margin: 0 }}>
							Pricing Data ({totalCount} records)
						</Typography.Title>
					</Col>
					<Col>
						<Space>
							{selectedRowKeys.length > 0 && (
								<Button type='primary' onClick={handleBulkEdit}>
									Bulk Edit ({selectedRowKeys.length})
								</Button>
							)}
						</Space>
					</Col>
				</Row>

				<Table
					columns={columns}
					dataSource={data}
					rowKey='_id'
					loading={loading}
					scroll={{ x: 1400 }}
					pagination={{
						current: currentPage,
						total: totalCount,
						pageSize: pageSize,
						showSizeChanger: true,
						showQuickJumper: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
						onChange: onPaginationChange,
						onShowSizeChange: onPaginationChange,
					}}
					rowSelection={rowSelection}
					size='small'
				/>
			</Card>

			{/* Bulk Edit Modal */}
			<Modal
				title='Bulk Edit Pricing'
				visible={bulkEditVisible}
				onCancel={() => setBulkEditVisible(false)}
				footer={null}
				width={600}
			>
				<BulkEditForm
					onSubmit={handleBulkEditSubmit}
					onCancel={() => setBulkEditVisible(false)}
				/>
			</Modal>
		</>
	)
}

export default PricingTable
